import moment from 'moment';
import momentDurationFormatSetup from 'moment-duration-format';
import { useEffect, useRef, useState } from 'react';
import Modal from 'react-modal';
import { useSelector } from 'react-redux';
import 'react-toastify/dist/ReactToastify.css';
import { selectApiBaseUrl } from '~/store';
import decodeURIComponentSafe from '../helpers/decodeURIComponentSafe';
import get from '../helpers/get';
import { heroStreamShape } from '../helpers/propTypeShapes';

const modalStyles = {
  content: {
    backgroundColor: 'rgb(246,246,246)',
    borderRadius: '10px',
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    minWidth: '312px',
    padding: '20px',
    textAlign: 'center',
    transform: 'translate(-50%, -50%)',
  },
  overlay: {
    backgroundColor: 'rgb(0,0,0,0.5)',
  },
};

momentDurationFormatSetup(moment);

const RecordingClickToPlay = ({ stream }) => {
  const apiBaseUrl = useSelector(selectApiBaseUrl);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [recordingTitle, setRecordingTitle] = useState(
    () =>
      decodeURIComponentSafe(stream?.broadcast?.name ?? '') ||
      decodeURIComponentSafe(stream?.title ?? '') ||
      [decodeURIComponentSafe(stream?.user?.name ?? ''), decodeURIComponentSafe(stream?.user?.location ?? '')].join(
        ', '
      )
  );
  const [newRecordingTitle, setNewRecordingTitle] = useState(recordingTitle);
  const element = useRef(null);

  const clickToPlay = (event) => {
    const player = event.target;

    if (player.currentTime === 0) {
      player.setAttribute('controls', true);
      player.load();

      player.addEventListener('loadedmetadata', () => {
        const playPromise = player.play();

        player.classList.add('loaded');
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              // We good
            })
            .catch((error) => {
              // toast.info('Playback failed. ',error);
              // eslint-disable-next-line no-console
              console.error('playback failed. ', error);
            });
        }
      });
    } else {
      event.preventDefault();
      if (player.paused) {
        const playPromise = player.play();

        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              // We good
            })
            .catch((error) => {
              // toast.info('Playback failed. ',error);
              // eslint-disable-next-line no-console
              console.error('playback failed. ', error);
            });
        }
      } else {
        player.pause();
      }
    }
  };

  const closeModal = () => {
    setModalIsOpen(false);
  };

  const cancelModal = () => {
    setNewRecordingTitle(recordingTitle);
    closeModal();
  };

  const hideDuration = (event) => {
    const parentDiv = event.target.closest('.stream-player-container');
    const targetClass = parentDiv.className;

    if (!targetClass.includes(' hide-duration')) {
      parentDiv.className += ' hide-duration';
    }
  };

  const observePlayer = (player) => {
    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!player.webkitDisplayingFullscreen) {
            if (entry.intersectionRatio < 0.08) {
              player.pause();
              obs.unobserve(player);
            }
          }
        });
      },
      { threshold: 0 }
    );
    observer.observe(player);
  };

  const openModal = () => {
    setModalIsOpen(true);
  };

  const renameRecording = () => {
    if (!newRecordingTitle) {
      closeModal();
      return;
    }

    const params = {
      method: 'PATCH',
      headers: {
        authorization: sessionStorage.getItem('authToken'),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        title: newRecordingTitle,
      }),
    };

    fetch(`${apiBaseUrl}/streams/${stream.id}`, params)
      .then((response) => {
        if (response.status !== 200) {
          setRecordingTitle(newRecordingTitle);
        }
        cancelModal();
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('streams PATCH API call failed. ', error);
      });
  };

  const updateRecordingTitle = (event) => {
    setNewRecordingTitle(decodeURIComponentSafe(event.target.value));
  };

  useEffect(() => {
    const player = element.current;
    player.addEventListener('playing', (event) => {
      const newPlayerPlaying = new CustomEvent('newPlayerPlaying', {
        detail: player.id,
      });

      hideDuration(event);
      player.classList.add('loaded');
      observePlayer(player);
      window.dispatchEvent(newPlayerPlaying);
    });

    return () => {
      player.removeEventListener('playing', (event) => {
        const newPlayerPlaying = new CustomEvent('newPlayerPlaying', {
          detail: player.id,
        });

        hideDuration(event);
        player.classList.add('loaded');
        observePlayer(player);
        window.dispatchEvent(newPlayerPlaying);
      });
    };
  }, []);

  const getOutFileSrc = ({ outfile }) => {
    if (outfile && outfile.endsWith('mp4')) {
      return outfile.replace(window.CONFIG.recordingClipsUrl, window.CONFIG.recordingClipsSourceUrl);
    }
    return null;
  };

  const recordingOwnerId = stream.user.id;

  // Limit renaming to only local recordings owned by the user for now,
  // since Portal needs to be updated to support titles for recordings
  // other than these.
  const shouldShowRenameModal = !(
    stream.broadcast ||
    (sessionStorage.getItem('currentAccountRole') !== 'ADMIN' &&
      parseInt(sessionStorage.getItem('userId'), 10) !== recordingOwnerId)
  );

  const broadcastOwnerId = get(stream, 'broadcast.user.id', null);
  const thumbnail = get(stream, 'thumbnail', '/FPO_video_thumb.png');
  const streamId = get(stream, 'broadcast.id', stream.id);

  const formattedDuration = moment.duration(stream.durationSeconds, 'seconds').format();

  const sourceType = stream.m3u8Url ? 'application/x-mpegURL' : 'video/mp4';
  const sourceURL = stream.m3u8Url || getOutFileSrc(stream);

  let viewerRole = ' recorded';
  if (stream.broadcast) {
    viewerRole = broadcastOwnerId === recordingOwnerId ? ' broadcast' : ' was a guest';
  }

  return (
    <li className="grid-broadcast-item clickToPlay" data-stream-id={streamId} data-stream-type="stream">
      <div className="stream-player-container">
        <video
          id={`player_${stream.id}`}
          className="video-js"
          onClick={clickToPlay}
          preload="none"
          poster={thumbnail}
          playsInline
          ref={element}
          src={sourceURL}
          type={sourceType}
        />
        {stream.durationSeconds && <div className="stream-duration">{formattedDuration}</div>}
      </div>
      <div className="video-info">
        <span className="grid-broadcast-name">{recordingTitle}</span>
        <span className="byline recording-info">
          {decodeURIComponentSafe(stream.user.name)}
          {viewerRole}
          &nbsp;
          {moment(stream.startedAt).format('MMM Do YYYY, h:mm a')}
        </span>
        {shouldShowRenameModal && (
          <button
            type="button"
            className="action-button"
            data-recording-id={streamId}
            onClick={openModal}
            aria-label="More Options"
          />
        )}
      </div>
      {shouldShowRenameModal && (
        <Modal isOpen={modalIsOpen} onRequestClose={() => closeModal} style={modalStyles}>
          <div className="modal-content">
            <h3>Edit title</h3>
            <input className="recording-title-input" onChange={updateRecordingTitle} value={newRecordingTitle} />
            <ul className="modal-buttons">
              <li>
                <button type="button" className="btn text" onClick={cancelModal}>
                  Cancel
                </button>
              </li>
              <li>
                <button type="submit" className="btn text" data-recording-id={streamId} onClick={renameRecording}>
                  Ok
                </button>
              </li>
            </ul>
          </div>
        </Modal>
      )}
    </li>
  );
};

RecordingClickToPlay.propTypes = {
  stream: heroStreamShape.isRequired,
};

export default RecordingClickToPlay;
